'use client'

import { useEffect, useMemo, useState } from 'react'
import { IconUser } from '@tabler/icons-react'
import { object, string } from 'yup'

import Input from 'shared/ui/form/Input'
import { validateField, validateValues } from 'shared/utils/form'

import { useCustomerData } from '../hooks/useCustomerData'

import ProfileDetailsSingleItem from './ProfileDetailsSingleItem'

interface FormPayload {
  firstName: string
  lastName: string
}

const schema = object().shape({
  firstName: string()
    .required('Imię jest wymagane')
    .min(3, 'Imię zbyt krótkie')
    .matches(/^\D*$/, {
      message: 'Nieprawidłowy format imienia'
    }),
  lastName: string()
    .required('Nazwisko jest wymagane')
    .min(3, 'Nazwisko zbyt krótkie')
    .matches(/^[A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż]+(-[A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż]+)*$/, {
      message: 'Nieprawidłowy format nazwiska'
    })
})

const validateFormField = (values: FormPayload) => validateField(schema, values)
const validateFormValues = validateValues(schema)

const initialValues = {
  firstName: '',
  lastName: ''
}

const CustomerNameUpdateForm = () => {
  const [values, setValues] = useState(initialValues)
  const [valid, setValid] = useState(false)
  const { user } = useCustomerData()

  const getInitialData = () => {
    if (user) {
      setValues({
        firstName: user.firstName,
        lastName: user.lastName
      })
    }
  }

  useEffect(() => {
    getInitialData()
  }, [user])

  const handleValueChange = (name: string, value: string) => {
    setValues(prev => ({ ...prev, [name]: value }))
  }

  useEffect(() => {
    values && validateFormValues(values, setValid)
  }, [values])

  // Memoize the validation function to prevent infinite re-renders
  const memoizedValidateFormField = useMemo(() => {
    return validateFormField(values)
  }, [values])

  return (
    <ProfileDetailsSingleItem
      id='profile_edit_name'
      modalTitle='Edytuj imię i nazwisko'
      isSubmitDisabled={!valid}
      icon={<IconUser />}
      label='Imię i nazwisko'
      value={`${user?.firstName || ''} ${user?.lastName || ''}`}
      values={values}
      openCallback={getInitialData}
    >
      <Input
        id='user_name_update_first_name'
        size='medium'
        name='firstName'
        label='Imię'
        required
        inputProps={{ autoComplete: 'given-name' }}
        value={values.firstName}
        onChange={handleValueChange}
        validate={memoizedValidateFormField}
      />
      <Input
        id='user_name_update_last_name'
        size='medium'
        name='lastName'
        label='Nazwisko'
        required
        inputProps={{ autoComplete: 'family-name' }}
        value={values.lastName}
        onChange={handleValueChange}
        validate={memoizedValidateFormField}
      />
    </ProfileDetailsSingleItem>
  )
}

export default CustomerNameUpdateForm
